import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import api from './api';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';

const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 pr-11 text-sm outline-none focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
const wrap = 'flex min-h-[70vh] items-center justify-center bg-[#e9f3ec] px-5';
const card = 'w-full max-w-md rounded-2xl bg-white p-8 shadow-md';

export const PasswordField = ({ value, onChange, placeholder = 'Password', testid = 'password' }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} type={show ? 'text' : 'password'} required minLength={6} className={inputCls} value={value} onChange={onChange} placeholder={placeholder} />
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1b4f72]">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
    </div>
  );
};

export const ForgotPassword = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post('/auth/forgot-password', { email, origin: window.location.origin }); } catch (err) {}
    setSent(true);
  };
  return (
    <div className={wrap}><div className={card} data-testid="forgot-password-page">
      <h1 className="ea-heading text-2xl font-bold">Forgot your password?</h1>
      {sent ? (
        <p className="mt-4 text-sm text-gray-600" data-testid="forgot-sent">If an account exists for that email, we've sent a reset link. Please check your inbox (and spam). The link expires in 1 hour.</p>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <p className="text-sm text-gray-500">Enter your email and we'll send you a secure reset link.</p>
          <input data-testid="forgot-email" type="email" required className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#3a9b5c]" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          <button data-testid="forgot-submit" className="w-full rounded-full bg-[#3a9b5c] px-6 py-3 font-semibold text-white hover:bg-[#318150]">Send Reset Link</button>
          <Link to="/auth" className="block text-center text-sm text-[#3a9b5c] underline">Back to login</Link>
        </form>
      )}
    </div></div>
  );
};

export const ResetPassword = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const token = params.get('token');
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    setBusy(true);
    try { await api.post('/auth/reset-password', { token, password: pw }); toast({ title: 'Password updated', description: 'You can now log in.' }); navigate('/auth'); }
    catch (err) { toast({ title: 'Error', description: err?.response?.data?.detail || 'Reset failed.', variant: 'destructive' }); } finally { setBusy(false); }
  };
  return (
    <div className={wrap}><div className={card} data-testid="reset-password-page">
      <h1 className="ea-heading text-2xl font-bold">Set a new password</h1>
      {!token ? <p className="mt-4 text-sm text-red-600">This reset link is invalid.</p> : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <PasswordField value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" testid="reset-password" />
          <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" testid="reset-confirm" />
          <button disabled={busy} data-testid="reset-submit" className="w-full rounded-full bg-[#3a9b5c] px-6 py-3 font-semibold text-white hover:bg-[#318150] disabled:opacity-60">{busy ? 'Saving…' : 'Update Password'}</button>
        </form>
      )}
    </div></div>
  );
};

export const ChangePassword = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  if (!loading && !user) { navigate('/auth'); return null; }
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    setBusy(true);
    try { await api.post('/auth/change-password', { currentPassword: cur, newPassword: pw }); toast({ title: 'Password changed' }); setCur(''); setPw(''); setConfirm(''); }
    catch (err) { toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed.', variant: 'destructive' }); } finally { setBusy(false); }
  };
  return (
    <div className={wrap}><div className={card} data-testid="change-password-page">
      <h1 className="ea-heading text-2xl font-bold">Change your password</h1>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <PasswordField value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" testid="current-password" />
        <PasswordField value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" testid="new-password" />
        <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" testid="confirm-password" />
        <button disabled={busy} data-testid="change-submit" className="w-full rounded-full bg-[#3a9b5c] px-6 py-3 font-semibold text-white hover:bg-[#318150] disabled:opacity-60">{busy ? 'Saving…' : 'Change Password'}</button>
      </form>
    </div></div>
  );
};
